import { useMemo } from 'react';

import type { IImage } from '@/types/content';

const FALLBACK = { url: '/icons/logo.png', width: '128' };

interface PropertyImageProps {
	image: IImage | undefined;
	/** The `sizes` hint for the browser, e.g. `(min-width: 992px) 60vw, 100vw`. */
	sizes?: string;
	alt?: string;
	className?: string;
	loading?: 'lazy' | 'eager';
	fetchPriority?: 'high' | 'low' | 'auto';
	onClick?: () => void;
}

/**
 * A property photo with a `srcSet` built from every format the image service
 * produced, so the browser downloads the smallest file that fills the slot.
 */
export const PropertyImage = ({
	image,
	sizes = '100vw',
	alt,
	className,
	loading = 'lazy',
	fetchPriority,
	onClick,
}: PropertyImageProps) => {
	const { src, srcSet } = useMemo(() => {
		if (!image?.formats) {
			return {
				src: FALLBACK.url,
				srcSet: `${FALLBACK.url} ${FALLBACK.width}w`,
			};
		}
		const candidates = Object.values(image.formats)
			.filter(({ name }) => name !== 'original' && name.includes('x'))
			.map(({ url, name }) => `${url} ${name.split('x')[0]}w`);
		return {
			src:
				image.formats[image.original]?.url ??
				Object.values(image.formats)[0]?.url,
			srcSet: candidates.join(', '),
		};
	}, [image]);

	return (
		// eslint-disable-next-line @next/next/no-img-element -- images come from S3 with their own formats; next/image would re-encode them
		<img
			src={src}
			srcSet={srcSet || undefined}
			sizes={sizes}
			alt={alt ?? image?.caption ?? ''}
			className={className}
			loading={loading}
			// React 18 only knows the lowercase attribute name.
			{...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
			decoding="async"
			onClick={onClick}
		/>
	);
};

export default PropertyImage;
